import {
    CloseOutlined,
    MessageOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { Button, List, notification, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ConversationListItemDto } from '@clh/api-client';

import { useApiClient } from '../hooks/use-api-client';

interface ChatSidebarProps {
    activeConversationId: string | null;
    onNewConversation: () => void;
    onNavigate?: () => void;
}

export function ChatSidebar({
    activeConversationId,
    onNewConversation,
    onNavigate,
}: ChatSidebarProps) {
    const navigate = useNavigate();
    const api = useApiClient();
    const [conversations, setConversations] = useState<
        ConversationListItemDto[]
    >([]);

    const fetchConversations = useCallback(() => {
        if (!api) return;
        api.adminChatControllerListConversations()
            .then(setConversations)
            .catch(() => {
                notification.error({
                    message: 'Failed to load conversations',
                    duration: 3,
                });
            });
    }, [api]);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    const deleteConversation = useCallback(
        (id: string) => {
            if (!api) return;
            api.adminChatControllerDismissConversation({ conversationId: id })
                .then(() => {
                    setConversations((prev) => prev.filter((c) => c.id !== id));
                    if (activeConversationId === id) {
                        navigate('/chat');
                    }
                })
                .catch(() => {
                    notification.error({
                        message: 'Failed to dismiss conversation',
                        duration: 3,
                    });
                });
        },
        [api, activeConversationId, navigate]
    );

    // Refetch when active conversation changes (e.g. new conversation created)
    useEffect(() => {
        if (!activeConversationId) return;
        fetchConversations();
    }, [activeConversationId, fetchConversations]);

    return (
        <div
            style={{
                width: onNavigate ? '100%' : 260,
                borderRight: onNavigate ? undefined : '1px solid #e8e8e8',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <style>{`.chat-sidebar-item:hover .chat-sidebar-delete { opacity: 1 !important; } .chat-sidebar-delete:hover { color: #ff4d4f !important; }`}</style>
            <div style={{ padding: '12px 16px' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    block
                    disabled={!activeConversationId}
                    onClick={onNewConversation}
                >
                    New Conversation
                </Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <Typography.Text
                    type="secondary"
                    style={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        padding: '8px 16px 4px',
                        display: 'block',
                    }}
                >
                    Chat History
                </Typography.Text>
                {(!activeConversationId ||
                    !conversations.some(
                        (c) => c.id === activeConversationId
                    )) && (
                    <List.Item
                        style={{
                            padding: '8px 16px',
                            background: '#e6f4ff',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                width: '100%',
                            }}
                        >
                            <MessageOutlined
                                style={{
                                    fontSize: 12,
                                    color: '#8c8c8c',
                                    flexShrink: 0,
                                }}
                            />
                            <Typography.Text
                                style={{ fontSize: 13, fontStyle: 'italic' }}
                                type="secondary"
                            >
                                New Conversation
                            </Typography.Text>
                        </div>
                    </List.Item>
                )}
                <List
                    locale={{ emptyText: ' ' }}
                    dataSource={conversations}
                    renderItem={(conv) => (
                        <List.Item
                            key={conv.id}
                            className="chat-sidebar-item"
                            onClick={() => {
                                navigate(`/chat/${conv.id}`);
                                onNavigate?.();
                            }}
                            style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                background:
                                    conv.id === activeConversationId
                                        ? '#e6f4ff'
                                        : undefined,
                                position: 'relative',
                            }}
                        >
                            <div
                                style={{
                                    overflow: 'hidden',
                                    width: '100%',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}
                                >
                                    <MessageOutlined
                                        style={{
                                            fontSize: 12,
                                            color: '#8c8c8c',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography.Text
                                        ellipsis
                                        style={{ fontSize: 13, flex: 1 }}
                                    >
                                        {conv.title ?? 'Untitled'}
                                    </Typography.Text>
                                    <CloseOutlined
                                        className="chat-sidebar-delete"
                                        style={{
                                            fontSize: 10,
                                            color: '#bfbfbf',
                                            flexShrink: 0,
                                            padding: 4,
                                            opacity: 0,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteConversation(conv.id);
                                        }}
                                    />
                                </div>
                                <Typography.Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        marginLeft: 20,
                                    }}
                                >
                                    {new Date(
                                        conv.createdAt
                                    ).toLocaleDateString()}
                                </Typography.Text>
                            </div>
                        </List.Item>
                    )}
                />
            </div>
        </div>
    );
}
